import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChatLinks } from './links';
import { Alert, Box, Button, Chip, Paper, Stack, TextField, Typography } from '@mui/material';
import { useDecidePlan } from '@diggens/shared-hooks';
import type { PlanDecision, PlanStep, PlanStreamEvent } from '@diggens/types';
import { PlanSteps } from './PlanSteps';
import type { StepStatus } from './reduceEvents';
import { useNow } from './time';

/** A plan as the chat stream has described it so far. */
export interface PlanCardState {
  planId: string;
  goal: string;
  requiresApproval: boolean;
  expiresAt: string | null;
  steps: PlanStep[];
  stepStatus: Record<string, StepStatus>;
  decision: PlanDecision | null;
  note: string | null;
  /** Nobody decided while the chat waited: it stays pending, and runs once it is approved. */
  pending: boolean;
}

export function applyPlanEvent(plans: Record<string, PlanCardState>, event: PlanStreamEvent): Record<string, PlanCardState> {
  if (event.type === 'plan_proposed') {
    const { planId, goal, requiresApproval, expiresAt, steps } = event;
    return { ...plans, [planId]: { planId, goal, requiresApproval, expiresAt, steps, stepStatus: {}, decision: null, note: null, pending: false } };
  }
  const plan = plans[event.planId];
  if (!plan) return plans;
  if (event.type === 'plan_decided') return { ...plans, [plan.planId]: { ...plan, decision: event.decision, note: event.note ?? null } };
  if (event.type === 'plan_pending') return { ...plans, [plan.planId]: { ...plan, pending: true } };
  return { ...plans, [plan.planId]: { ...plan, stepStatus: { ...plan.stepStatus, [event.stepId]: event.status } } };
}

const DECISION_LABELS: Record<PlanDecision, string> = { approved: 'Approved', rejected: 'Rejected', expired: 'Expired: nobody decided in time' };

/** The Manager's plan in the chat, with Approve and Reject while it waits for approval. */
export function PlanCard({ plan }: { plan: PlanCardState }) {
  const navigate = useNavigate();
  const { planPath } = useChatLinks();
  const decide = useDecidePlan();
  const [note, setNote] = useState('');
  const now = useNow();
  const waiting = plan.requiresApproval && !plan.decision;
  const remaining = plan.expiresAt ? Date.parse(plan.expiresAt) - now : 0;
  const decided = decide.data?.decision ?? plan.decision;

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 1, borderLeft: 4, borderLeftColor: waiting ? 'warning.main' : 'primary.main' }}>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Plan: {plan.goal}
        </Typography>
        {decided && <Chip label={DECISION_LABELS[decided]} size="small" color={decided === 'approved' ? 'success' : 'error'} variant="outlined" />}
        {waiting && plan.pending && !decided && <Chip label="Pending approval" size="small" color="warning" variant="outlined" />}
        {!plan.requiresApproval && <Chip label={plan.steps.some((step) => step.writes) ? 'Runs now, no approval needed' : 'Read-only, runs now'} size="small" variant="outlined" />}
      </Stack>

      <PlanSteps steps={plan.steps.map((step) => ({ ...step, status: plan.stepStatus[step.id] ?? 'queued' }))} showTasks={waiting} />

      {waiting && !decide.isSuccess && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="warning.main" sx={{ fontWeight: 600 }}>
            {remaining <= 0
              ? 'Approval window has closed.'
              : plan.pending
                ? `Pending. You can approve it here if it only writes to your own HubSpot; otherwise an admin decides it in Activity. Your Chief of Staff carries it out as soon as it is decided, and replies in this chat.`
                : `Needs approval: yours if it only writes to your own HubSpot, otherwise an admin’s. If nobody decides within 3 minutes, it stays pending for up to 7 days.`}
          </Typography>
          <TextField
            label="Note for your Chief of Staff (optional)"
            size="small"
            fullWidth
            value={note}
            onChange={(event) => setNote(event.target.value)}
            slotProps={{ htmlInput: { maxLength: 500 } }}
            sx={{ mt: 1.5 }}
          />
          {decide.error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {decide.error.message}
            </Alert>
          )}
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <Button
              size="small"
              variant="contained"
              disabled={decide.isPending || remaining <= 0}
              onClick={() => decide.mutate({ planId: plan.planId, decision: 'approve', note: note.trim() || undefined })}
            >
              Approve
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={decide.isPending || remaining <= 0}
              onClick={() => decide.mutate({ planId: plan.planId, decision: 'reject', note: note.trim() || undefined })}
            >
              Reject
            </Button>
            {planPath && (
              <Button size="small" onClick={() => navigate(planPath(plan.planId))}>
                Open in Activity
              </Button>
            )}
          </Stack>
        </Box>
      )}
      {plan.pending && decide.isSuccess && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Your Chief of Staff is picking this up now; its reply shows in this conversation when you open it again from the list.
        </Typography>
      )}
      {plan.note && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Approver’s note: {plan.note}
        </Typography>
      )}
    </Paper>
  );
}
